"use client"

import { useEffect, useState } from "react"
import { Routes, Route, useNavigate, useLocation } from "react-router-dom"
import { useMsal } from "@azure/msal-react"
import Home from "./components/pages/HomePage/Home"
import Service from "./components/pages/Service/Service"
import SignUp from "./components/SignUp/SignUp"
import PrivateRoute from "./routes/PrivateRoute.js"
import Header from "./components/header/Header"
import Footer from "./components/Footer/Footer"
import ScrollToTop from "./components/ScrollToTop/ScrollToTop"
import { handleMicrosoftResponse, clearPendingInteractions } from "./Auth/AuthMicrosoft/authMicrosoft"
import LoadingScreen from "./components/SignUp/LoadingScreen.js" // Importer le LoadingScreen
import AOS from 'aos';
import 'aos/dist/aos.css';
import { initAOS } from './utils/aos';
import { getSessionConfig } from './lib/api';
import { devError, devLog, devWarn } from './utils/devConsole';

function App() {
  const { instance } = useMsal() // MSAL instance will be provided by MsalProvider
  const navigate = useNavigate()
  const location = useLocation()
  const [isAuthenticated, setIsAuthenticated] = useState(false) // Inizializzazione a false per default
  const [isInitializing, setIsInitializing] = useState(true)
  const [sessionConfig, setSessionConfig] = useState({ sessionDurationMinutes: 60 })
  const [hasLoggedOut, setHasLoggedOut] = useState(false) // Flag per evitare riconnessioni automatiche

  // Gestione ?token= nell'URL (ritorno dal backend dopo login Microsoft)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tokenFromUrl = params.get("token")
    if (tokenFromUrl) {
      localStorage.setItem("authToken", tokenFromUrl)
      localStorage.setItem("authMethod", "microsoft")
      localStorage.setItem("connectionTime", Date.now().toString())
      window.history.replaceState({}, document.title, window.location.pathname)
      navigate("/service", { replace: true })
      setIsAuthenticated(true)
      setIsInitializing(false)
      return
    }
  }, [navigate])

  // Redirect /signin → /service se già autenticato
  useEffect(() => {
    if (location.pathname === "/signin" && localStorage.getItem("authToken")) {
      navigate("/service", { replace: true })
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    let isMounted = true; // Flag per evitare aggiornamenti di stato su componente smontato

    const initializeAuth = async () => {
      if (!isMounted) return;
      
      // Se abbiamo già gestito ?token=, non rieseguire
      const params = new URLSearchParams(window.location.search)
      if (params.get("token")) return;
      
      devLog("Verifica dello stato di autenticazione...")

      try {
        // Recuperare la configurazione di sessione dal backend (una sola volta)
        if (!sessionConfig.sessionDurationMinutes) {
          const config = await getSessionConfig();
          if (isMounted) {
            setSessionConfig(config);
            devLog("🔧 [CONFIG] Session duration:", config.sessionDurationMinutes, "minutes");
          }
        }

        // Pulire solo le interazioni Microsoft se l'utente era connesso con Microsoft
        const authMethod = localStorage.getItem("authMethod");
        if (instance && authMethod === "microsoft") {
          await clearPendingInteractions(instance)
        }

        const token = localStorage.getItem("authToken")

        if (token) {
          devLog("Token trovato, l'utente è autenticato")
          if (isMounted) {
            setIsAuthenticated(true) // Se un token è trovato, l'utente è autenticato
          }
        } else if (instance && !hasLoggedOut) {
          devLog("Istanza MSAL rilevata, gestione della promessa di reindirizzamento...")
          const response = await instance.handleRedirectPromise()

          if (response && response.account) {
            devLog("Risposta valida ricevuta, gestione della risposta Microsoft...")
            await handleMicrosoftResponse(instance, navigate)
            if (isMounted) {
              setIsAuthenticated(true)
            }
          } else {
            devWarn("Nessuna risposta di autenticazione valida ricevuta.")
            if (isMounted) {
              setIsAuthenticated(false)
            }
          }
        } else {
          if (hasLoggedOut) {
            devLog("L'utente ha effettuato il logout, saltando la gestione del reindirizzamento MSAL")
          } else {
            devError("Istanza MSAL non trovata.")
          }
          if (isMounted) {
            setIsAuthenticated(false)
          }
        }
      } catch (error) {
        devError("Errore durante l'inizializzazione dell'autenticazione:", error)
        if (isMounted) {
          setIsAuthenticated(false)
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false)
        }
      }
    }

    // Eseguire solo una volta al montaggio del componente
    if (isInitializing) {
      initializeAuth()
    }

    // Funzione di pulizia
    return () => {
      isMounted = false;
    };
  }, []) // Dipendenze vuote per evitare riesecuzioni

  // Scroll to section when URL has a hash (e.g. /#chisiamo).
  // Needed because footer links must work also from /service.
  useEffect(() => {
    const hash = location.hash ? location.hash.replace("#", "") : "";
    if (!hash) return;

    const offsetById = {
      chisiamo: -150,
      diventacliente: -75,
      cashcarry: -150,
      assortimento: -150,
      marchi: -175,
      iscrivi: -75, // backward compatibility (typo)
      iscriviti: -75,
    };

    let attempts = 0;
    const maxAttempts = 25;

    const tryScroll = () => {
      attempts += 1;
      const el = document.getElementById(hash);
      if (!el) {
        if (attempts < maxAttempts) setTimeout(tryScroll, 80);
        return;
      }

      const offset = offsetById[hash] ?? -120;
      const top = el.getBoundingClientRect().top + window.pageYOffset + offset;
      window.scrollTo({ top, behavior: "smooth" });
    };

    // Let the route render first
    setTimeout(tryScroll, 0);
  }, [location.pathname, location.hash]);

  // Listener per rilevare i cambiamenti di token in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        if (e.newValue) {
          devLog("🔑 [TOKEN-DETECTED] Token rilevato, aggiornamento dello stato di autenticazione")
          setIsAuthenticated(true)
          // Keep current page; only avoid staying on the sign-in page after login
          if (location.pathname === "/signin") navigate("/service")
        } else {
          devLog("🔑 [TOKEN-REMOVED] Token rimosso, disconnessione")
          setIsAuthenticated(false)
          // Non reindirizzare se siamo su /signin
          if (location.pathname !== "/signin") {
            navigate("/")
          }
        }
      }
    }

    // Ascoltare i cambiamenti di localStorage
    window.addEventListener('storage', handleStorageChange)
    
    // Ascoltare i cambiamenti di localStorage nella stessa scheda - DISATTIVATO
    // const checkToken = () => { ... } // Funzione commentata

    // Verificare il token periodicamente - DISATTIVATO per evitare reindirizzamenti intempestivi
    // const interval = setInterval(checkToken, 1000) // Commentato

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      // clearInterval(interval) // Commentato
    }
  }, [isAuthenticated, navigate, location.pathname])

  useEffect(() => {
    // Initialize AOS with configuration
    initAOS();

    return () => {
      window.removeEventListener('scroll', () => {
        AOS.refresh();
      });
    };
  }, []);

  // Verifica del token solo durante le azioni utente
  // Più verifiche periodiche - disconnessione automatica tramite chiamate API

  // Visualizzazione del caricamento durante la verifica dello stato di autenticazione
  if (isInitializing) {
    return <LoadingScreen /> // Mostrare la schermata di caricamento personalizzata
  }

  return (
    <div className="app-container">
      <Header />
      <main className="main-content">
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/signin" element={<SignUp />} />
          <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
            <Route path="/service" element={<Service />} />
          </Route>
        </Routes>
      </main>
      <Footer />
    </div>
  )
}

export default App
